import { useCallback, useEffect, useRef } from 'react';

/**
 * THE FOUR THINGS A DIALOG OWES A KEYBOARD — as one thing a dialog can USE.
 *
 * Focus goes in, focus stays in, Escape closes, focus comes back, and the page
 * behind it is inert. `AnimatedModal` already knew all of this, but it knew it
 * privately and behind an opt-in flag, so a dialog that was not an
 * `AnimatedModal` — or was one and did not tick the box — got none of it.
 * Five dialogs were in that state, and the worst of them was the checkout
 * login gate: the first Tab left the box, Tabs 2–9 walked the checkout form
 * BEHIND the dimmed overlay, and typing a phone number after eight Tabs put
 * the digits into the shipping phone field, which has `outline: none`, so they
 * appeared nowhere at all. On a store where the phone number IS the login
 * credential, that is an order nobody can deliver.
 *
 * ── WHY A HOOK AND NOT A COMPONENT ────────────────────────────────────────
 *
 * The broken dialogs do not share a shape. One is a portal into `#modal-root`,
 * one is a fullscreen media viewer with its own drag-to-dismiss transform, one
 * is an `AnimatedModal`. Asking each to become the same component is a rewrite
 * of three unrelated files; asking each for a ref and a close callback is two
 * lines. A hook is what the difference between those two costs.
 *
 * ── INERT, NOT aria-hidden ────────────────────────────────────────────────
 *
 * The background is switched off with the `inert` attribute: it removes the
 * subtree from the tab order AND from the accessibility tree in one attribute,
 * which is the thing `aria-modal="true"` has been *claiming* all along. It is
 * deliberately NOT paired with `aria-hidden`: the two say the same thing in
 * browsers that implement `inert`, and in a test renderer (jsdom implements
 * neither) `aria-hidden` would additionally hide the page from every
 * `getByRole` query in the suite — a test-only behaviour change bought for no
 * user-facing gain. Tab containment below is what makes this correct
 * everywhere regardless.
 *
 * ── THE STACK ─────────────────────────────────────────────────────────────
 *
 * Only the TOPMOST open dialog answers Tab and Escape. Without that, a
 * confirm-dialog opened from inside a sheet has two trap handlers fighting
 * over the same keypress and the outer one wins, because it was bound first.
 */

/** Everything the browser will let Tab land on, in DOM order. */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', 'iframe', 'audio[controls]', 'video[controls]',
  '[contenteditable]:not([contenteditable="false"])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Can Tab actually land here?
 *
 * NOT `offsetParent !== null`, which is what the old private copy of this
 * check used. `offsetParent` is a LAYOUT answer, and a test renderer has no
 * layout: in jsdom every element in the app reports `null`, so that filter
 * removed every control in the dialog and a "wraps Tab" assertion passed
 * against a trap that moved focus nowhere. Computed style answers the same
 * question, cascades through a `display:none` ancestor in a real browser, and
 * is truthful in both places.
 */
function isReachable(el) {
  if (el.hasAttribute('inert') || el.getAttribute('aria-hidden') === 'true') return false;
  if (typeof el.closest === 'function' && el.closest('[hidden],[inert]')) return false;
  if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
    const s = window.getComputedStyle(el);
    if (s && (s.display === 'none' || s.visibility === 'hidden')) return false;
  }
  return true;
}

/** Open dialogs, oldest first. The last entry owns the keyboard. */
const STACK = [];

const isTop = (token) => STACK.length > 0 && STACK[STACK.length - 1] === token;

/**
 * WHO HAD FOCUS BEFORE THIS DIALOG — WHICH IS NOT ALWAYS `document.activeElement`.
 *
 * Measured live on dk-store's search drawer: Escape closed it and focus landed
 * on `<body>` rather than on the search button. The focus trail said why. The
 * drawer's field carries `autoFocus`, React applies that during the COMMIT —
 * before any effect runs — so by the time this hook asked "who had focus?",
 * the honest answer was already "a control inside this dialog". On the way out
 * it handed focus back to a field that was being removed, and a focused
 * element leaving the document drops focus to `<body>`.
 *
 * So the answer is remembered rather than sampled: a short trail of recently
 * focused elements, and the opener is the most recent one that is NOT inside
 * the dialog. Every fixture in the unit suite focused nothing on mount, which
 * is why all of them passed and the real drawer did not.
 *
 * The listener is installed once, lazily, and never removed — it is one
 * capture-phase `focusin` handler writing into a four-element array.
 */
const TRAIL = [];
let trailWired = false;

function rememberFocus(el) {
  if (!el || el === document.body) return;
  const i = TRAIL.indexOf(el);
  if (i !== -1) TRAIL.splice(i, 1);
  TRAIL.unshift(el);
  if (TRAIL.length > 4) TRAIL.length = 4;
}

function wireTrail() {
  if (trailWired || typeof document === 'undefined') return;
  trailWired = true;
  rememberFocus(document.activeElement);
  document.addEventListener('focusin', (e) => rememberFocus(e.target), true);
}

/** The most recent focus that is still in the document and outside `root`. */
function openerFor(root) {
  const active = document.activeElement;
  if (active && active !== document.body && !root.contains(active)) return active;
  for (const el of TRAIL) {
    if (el !== root && !root.contains(el) && document.contains(el)) return el;
  }
  return null;
}

/**
 * Switch off everything under <body> that does not contain this dialog, and
 * KEEP it switched off for as long as the dialog is open.
 *
 * Returns the undo. Elements that ALREADY carried `inert` (an outer dialog's
 * doing, or a host page's) are left alone on the way out — restoring a value
 * we did not set is how nested dialogs un-inert the page underneath them.
 *
 * ── WHY AN OBSERVER AND NOT A SINGLE PASS ─────────────────────────────────
 *
 * Measured in a real browser on the Arabic checkout, with the login gate open:
 * three body children were still live — `#hf-cc-num-hidden`, `#hf-exp-hidden`
 * and an `<iframe>`. They are Tranzila's hosted card fields, and the payment
 * SDK appends them to `<body>` WHILE the dialog is already up, so a one-shot
 * pass at open time cannot see them. "The background is inert" has to stay
 * true for as long as the dialog is open, and on a checkout the things
 * arriving late are the card fields.
 */
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK', 'META']);

/**
 * EVERY DIALOG CURRENTLY HOLDING THE PAGE OFF -- BECAUSE A DIALOG IS NOT PAGE.
 *
 * The observer above exists for things that arrive at `<body>` late, and the
 * example it was written for is a payment SDK's card fields. But the other
 * thing that arrives at `<body>` late is ANOTHER DIALOG: every sheet in this
 * storefront is a portal, so a sort sheet opened from inside the search drawer
 * is, to the drawer's observer, an ordinary new body child -- and it was
 * switched off the moment it appeared.
 *
 * The owner found it: "in search, when clicking on sort by, the popup doesn't
 * hide when clicking on X." Measured on production, the sort sheet's overlay
 * carried `inert`, so nothing in it took a click -- not the X, not an option,
 * not the backdrop. Only Escape, which is bound on the document, still worked.
 *
 * So the open dialogs keep a register of their own elements, and two rules
 * fall out of it: nobody switches off a branch that holds a dialog, and a
 * dialog opening asks the ones already up to hand its own branch back (they
 * inerted it before it had registered -- the observer runs in a microtask, the
 * registering effect in a later task).
 */
const CURTAINS = [];

function inertBackground(container) {
  if (typeof document === 'undefined' || !container) return () => {};
  const touched = [];
  const curtain = { container, release: null };

  /* WHICH DIALOGS THIS ONE MAY NOT SWITCH OFF: the ones that opened AFTER it,
   * and only those. A dialog opened from inside another one is on top, so the
   * one underneath IS background and stays inert -- that part was always right,
   * and it is what `aria-modal` promises. The asymmetry is the whole fix: an
   * older dialog's observer must keep its hands off a newer dialog's element,
   * while a newer dialog goes on switching the older one off. */
  const newerThanThis = () => CURTAINS.slice(CURTAINS.indexOf(curtain) + 1);

  const switchOff = (el) => {
    if (!el || el.nodeType !== 1) return;
    if (el === container || el.contains(container)) return;
    if (SKIP_TAGS.has(el.tagName)) return;
    if (newerThanThis().some((c) => el === c.container || el.contains(c.container))) return;
    if (el.hasAttribute('inert')) return;
    el.setAttribute('inert', '');
    touched.push(el);
  };

  /** Hand a branch back -- a dialog has just opened inside it. */
  const release = (node) => {
    for (let i = touched.length - 1; i >= 0; i -= 1) {
      const el = touched[i];
      if (el === node || el.contains(node)) {
        el.removeAttribute('inert');
        touched.splice(i, 1);
      }
    }
  };

  /* Registered BEFORE the first sweep, so that during it this dialog is the
   * newest and nothing is exempt from it but itself. */
  curtain.release = release;
  CURTAINS.push(curtain);
  /* The dialogs already up switched this one off the moment its portal
   * appeared -- their observer runs in a microtask, this effect in a later
   * task, so they could not yet know it was a dialog. Ask for it back. */
  for (const other of CURTAINS) if (other !== curtain) other.release(container);

  for (const el of Array.from(document.body.children)) switchOff(el);

  let observer = null;
  if (typeof MutationObserver === 'function') {
    observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) switchOff(node);
      }
    });
    observer.observe(document.body, { childList: true });
  }

  return () => {
    if (observer) observer.disconnect();
    const i = CURTAINS.indexOf(curtain);
    if (i !== -1) CURTAINS.splice(i, 1);
    for (const el of touched) el.removeAttribute('inert');
  };
}

/**
 * @param {object}   opts
 * @param {boolean}  opts.active        is the dialog open right now
 * @param {object}   opts.containerRef  ref to the dialog element itself
 * @param {Function} opts.onEscape      what Escape means (usually: close)
 * @param {object}   [opts.initialFocusRef]  where focus should land, if not the dialog
 * @param {object}   [opts.returnFocusRef]   where focus goes back to, if not the opener
 */
export default function useDialogA11y({
  active,
  containerRef,
  onEscape,
  initialFocusRef = null,
  returnFocusRef = null,
}) {
  // Captured on the way IN, because by the time we leave, the element that
  // opened the dialog has long since lost focus to the dialog itself.
  const openerRef = useRef(null);
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;
  wireTrail();

  const focusables = useCallback(() => {
    const root = containerRef && containerRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter(isReachable);
  }, [containerRef]);

  /* Take focus, hand it back, and switch the page off — one effect, because
   * all three are the same lifetime and splitting them lets the restore run
   * in an order nobody chose. */
  useEffect(() => {
    if (!active) return undefined;
    const root = containerRef && containerRef.current;
    if (!root) {
      /* A dialog that says it is open and has nothing to point at gets NO
       * containment, and — because `active` does not change again — never gets
       * any. That is the failure this whole hook exists to end, so it is not
       * allowed to be silent. Almost always the fix is at the call site:
       * `active` must also wait for the element to be in the document. */
      if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn('[useDialogA11y] active with no container element — focus is NOT trapped.');
      }
      return undefined;
    }

    const token = {};
    STACK.push(token);
    openerRef.current = (returnFocusRef && returnFocusRef.current) || openerFor(root);
    const unInert = inertBackground(root);

    const target = (initialFocusRef && initialFocusRef.current) || null;
    if (target && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    } else {
      // The dialog itself, not its first control: a reader arriving at a login
      // gate should hear the dialog's name, not be dropped onto "Close".
      if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
      root.focus({ preventScroll: true });
    }

    return () => {
      unInert();
      const i = STACK.indexOf(token);
      if (i !== -1) STACK.splice(i, 1);
      const back = openerRef.current;
      if (back && typeof back.focus === 'function' && document.contains(back)) {
        back.focus({ preventScroll: true });
      }
      openerRef.current = null;
    };
    // `initialFocusRef`/`returnFocusRef` are refs — stable by construction, and
    // re-running this on a ref identity change would re-take focus mid-dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, containerRef]);

  /* Tab containment and Escape. Bound in the CAPTURE phase on the document so
   * it sees the key before whatever has focus does — including a field inside
   * the page behind the overlay, which is where focus was when this was
   * reported. */
  useEffect(() => {
    if (!active) return undefined;
    const root = containerRef && containerRef.current;
    if (!root) return undefined;
    const myToken = STACK[STACK.length - 1];

    const onKey = (e) => {
      if (!isTop(myToken)) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (escapeRef.current) escapeRef.current(e);
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      const active_ = document.activeElement;
      const inside = root.contains(active_);
      if (items.length === 0) {
        // Nothing to land on, but landing OUTSIDE is still wrong.
        e.preventDefault();
        root.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (!inside || active_ === first || active_ === root)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active_ === last)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [active, containerRef, focusables]);
}

export { FOCUSABLE };
